import * as React from 'react';

// Internal
import { BaseHelmet } from '@src/base/BaseHelmet';
import { BaseLayout } from '@src/base/BaseLayout/BaseLayout';
import { Section } from '@src/components/Section';

// Slides
import { BestResaleValue } from '@src/slides/BestResaleValue';
import { BrandsForEveryBudget } from '@src/slides/BrandsForEveryBudget';
import { ConsumersFeelProud } from '@src/slides/ConsumersFeelProud';
import { ConsumersHelp } from '@src/slides/ConsumersHelp';
import { ConsumersPrioritize } from '@src/slides/ConsumersPrioritize';
import { CovidRetailSlump } from '@src/slides/CovidRetailSlump';
import { CurrentResaleLandscape } from '@src/slides/CurrentResaleLandscape';
import { Disclaimer } from '@src/slides/Disclaimer';
import { EconomicUncertainty } from '@src/slides/EconomicUncertainty';
import { FinalQuote } from '@src/slides/FinalQuote';
import { Foreword } from '@src/slides/Foreword';
import { GenZPower } from '@src/slides/GenZPower';
import { InvestmentPieces } from '@src/slides/InvestmentPieces';
import { InvestorsGetSerious } from '@src/slides/InvestorsGetSerious';
import { Methodology } from '@src/slides/Methodology';
import { PoisedToAccelerate } from '@src/slides/PoisedToAccelerate';
import { ResaleMarketMassive } from '@src/slides/ResaleMarketMassive';
import { ResaleMomentum } from '@src/slides/ResaleMomentum';
import { ResaleMostEco } from '@src/slides/ResaleMostEco';
import { ResaleSolvesWaste } from '@src/slides/ResaleSolvesWaste';
import { RetailSectorDefs } from '@src/slides/RetailSectorDefs';
import { RetailSeeksToJoin } from '@src/slides/RetailSeeksToJoin';
import { RetailTurnsToResale } from '@src/slides/RetailTurnsToResale';
import { SecondhandDouble } from '@src/slides/SecondhandDouble';
import { SecondhandMarketGrowth } from '@src/slides/SecondhandMarketGrowth';
import { SecondhandShoppers } from '@src/slides/SecondhandShoppers';
import { SecondhandVsFastFashion } from '@src/slides/SecondhandVsFastFashion';
import { ShoppersShift } from '@src/slides/ShoppersShift';
import { Sources } from '@src/slides/Sources';
import { StigmaToStatus } from '@src/slides/StigmaToStatus';
import { StuckAtHome } from '@src/slides/StuckAtHome';
import { SustainabilityPriority } from '@src/slides/SustainabilityPriority';
import { ThredupWeathersCovid } from '@src/slides/ThredupWeathersCovid';
import { ThriftOffsetsWaste } from '@src/slides/ThriftOffsetsWaste';
import { TopResaleTrends } from '@src/slides/TopResaleTrends';

// Images
import warehouseImage from '@src/data/images/resaleReport-coverDivider-thumbanil-v2.jpg';
import warehouseVideo from '@src/data/images/resaleReport-coverDivider-v2.mp4';

/**
 * @name PageIndex
 * @description Index page for Resale Report
 * Note: this needs to be named `index.tsx` not `Index.tsx`
 */
const PageIndex: React.FC = () => (
  <BaseLayout>
    <BaseHelmet />

    <Section
      hero={{
        centered: true,
        title: '2020 Resale Report',
        video: { placeholder: warehouseImage, src: warehouseVideo }
      }}
      id="foreword"
    >
      <Foreword />
    </Section>

    <Section
      hero={{
        image: { alt: 'Thredup Warehouse', fileName: '1-divider.jpg' },
        sectionNumber: '01',
        title: 'Resale Growth & The Impact of Covid'
      }}
      id="resale-growth"
    >
      <ResaleMomentum />
      <SecondhandMarketGrowth />
      <CovidRetailSlump />
      <ThredupWeathersCovid />
      <StuckAtHome />
      <PoisedToAccelerate />
      <EconomicUncertainty />
      <ConsumersPrioritize />
      <SecondhandVsFastFashion />
      <RetailTurnsToResale />
    </Section>

    <Section
      hero={{
        image: { alt: 'Thredup Warehouse', fileName: '2-divider.jpg' },
        sectionNumber: '02',
        title: `Who's Thrifting and Why`,
        titleFulWidth: true
      }}
      id="consumer-trends"
    >
      <SecondhandShoppers />
      <ResaleMarketMassive />
      <GenZPower />
      <ConsumersFeelProud />
      <SustainabilityPriority />
      <StigmaToStatus />
    </Section>

    <Section
      hero={{
        image: { alt: 'Thredup Warehouse', fileName: '3-divider.jpg' },
        sectionNumber: '03',
        title: 'Resale is the Future of Fashion'
      }}
      id="future-of-fashion"
    >
      <ShoppersShift />
      <SecondhandDouble />
      <RetailSeeksToJoin />
      <InvestorsGetSerious />
    </Section>

    <Section
      hero={{
        image: { alt: 'Thredup Warehouse', fileName: '4-divider.jpg' },
        sectionNumber: '04',
        title: 'Resale is Good For The Planet'
      }}
      id="eco-impact"
    >
      <ResaleSolvesWaste />
      <ThriftOffsetsWaste />
      <ResaleMostEco />
      <ConsumersHelp />
    </Section>

    <Section
      hero={{
        image: { alt: 'Thredup Warehouse', fileName: '5-resellableBrands.jpg' },
        sectionNumber: '05',
        title: 'Best Brands for Resale',
        titleFulWidth: true
      }}
      id="resellable-brands"
    >
      <BestResaleValue />
      <BrandsForEveryBudget />
      <InvestmentPieces />
      <TopResaleTrends />
      <FinalQuote />
    </Section>

    <section id="methodology-and-sources">
      <main>
        <Methodology />
        <CurrentResaleLandscape />
        <RetailSectorDefs />
        <Disclaimer />
        <Sources />
      </main>
    </section>
  </BaseLayout>
);

export default PageIndex;
